import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import { useLocationStore } from "../../../store/masterDataStore";
import {
  useHazardousMaterialStore,
  useStorageHandlingStore,
} from "../../../store/hazardousMaterialStore";
import { useOrgUsersStore } from "../../../store/store";
import { OrgUserType, StorageHandlingType } from "../../../types/api";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgba(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const emptyForm: StorageHandlingType = {
  materialName: "",
  storageLocation: "",
  storageConditions: "",
  containerType: "",
  // storageCapacity: "",
  // currentQuantity: "",
  // storageDate: "",
  // handlingProcedures: "",
  ppeRequired: [],
  // spillResponseProcedures: "",
  // hazardLabelsPresent: false,
  // sdsAvailable: false,
  lastSafetyInspectionDate: "",
  inspectedBy: "",
  // incidentLogs: "",
  remarks: "",
  temperature: "",
  humidity: "",
};

const ppeOptions = ["Gloves", "Goggles", "Respirator", "Protective clothing"];

const containerTypes = ["Drum", "Bottle", "Tank", "Box", "Other"];

const StorageHandlingRecords: React.FC = () => {
  const [rowData, setRowData] = useState<StorageHandlingType[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<StorageHandlingType>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);

  const {
    storageHandlingRecords,
    loading,
    fetchAllStorageHandling,
    createStorageHandling,
    updateStorageHandling,
    deleteStorageHandling,
  } = useStorageHandlingStore();

  const {
    hazardousMaterials,
    materialDataMap,
    loading: loadingHazardousMaterials,
    fetchAllHazardousMaterials,
  } = useHazardousMaterialStore();

  const {
    orgUsersData,
    orgUsersMap,
    loading: loadingOrgUsers,
    fetchOrgUsers,
  } = useOrgUsersStore();

  const {
    allLocations,
    allLocationsMap,
    loading: loadingLocations,
    fetchAllLocations,
  } = useLocationStore();

  useEffect(() => {
    fetchAllStorageHandling();
    if (!hazardousMaterials.length) fetchAllHazardousMaterials();
    if (!orgUsersData.length) fetchOrgUsers();
    if (!allLocations.length) fetchAllLocations();
  }, [fetchAllStorageHandling]);

  useEffect(() => {
    setRowData(storageHandlingRecords ?? []);
  }, [storageHandlingRecords]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;

    if (type === "checkbox" && name === "hazardLabelsPresent") {
      setFormData((prev) => ({ ...prev, hazardLabelsPresent: checked }));
    } else if (type === "checkbox" && name === "sdsAvailable") {
      setFormData((prev) => ({ ...prev, sdsAvailable: checked }));
    } else if (type === "checkbox" && name === "ppeRequired") {
      // For PPE multiple checkboxes
      let newPPE: string[] = [...formData.ppeRequired];
      if (checked) {
        if (!newPPE.includes(value)) newPPE.push(value);
      } else {
        newPPE = newPPE.filter((ppe) => ppe !== value);
      }
      setFormData((prev) => ({ ...prev, ppeRequired: newPPE }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let success = false;
    if (isEditMode && editId) {
      success = await updateStorageHandling(formData, editId);
    } else {
      success = await createStorageHandling(formData);
    }
    if (success) {
      setModalOpen(false);
      setFormData(emptyForm);
      setEditId(null);
      setIsEditMode(false);
    }
  };

  const handleEdit = (data: StorageHandlingType) => {
    setIsEditMode(true);
    setFormData({
      materialName: data.materialName || "",
      storageLocation: data.storageLocation || "",
      storageConditions: data.storageConditions || "",
      containerType: data.containerType || "",
      // storageCapacity: data.storageCapacity || "",
      // currentQuantity: data.currentQuantity || "",
      // storageDate: data.storageDate || "",
      // handlingProcedures: data.handlingProcedures || "",
      ppeRequired: data.ppeRequired || [],
      // spillResponseProcedures: data.spillResponseProcedures || "",
      // hazardLabelsPresent: data.hazardLabelsPresent || false,
      // sdsAvailable: data.sdsAvailable || false,
      lastSafetyInspectionDate: data.lastSafetyInspectionDate || "",
      inspectedBy: data.inspectedBy || "",
      // incidentLogs: data.incidentLogs || "",
      temperature: data.temperature || "",
      humidity: data.humidity || "",
      remarks: data.remarks || "",
      _id: data._id,
    });
    setEditId(data._id || "");
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this storage & handling record?"
    );
    if (confirmed) {
      await deleteStorageHandling(id);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setFormData(emptyForm);
    setEditId(null);
    setIsEditMode(false);
  };

  const columnDefs = useMemo<any[]>(
    () => [
      {
        field: "materialName",
        headerName: "Material Name",
        minWidth: 150,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px", textAlign: "left" },
        valueGetter: (params: any) =>
          materialDataMap.get(params.data.materialName) || "N/A",
      },
      {
        field: "storageLocation",
        headerName: "Storage Location",
        minWidth: 140,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px", textAlign: "left" },
        valueGetter: (params: any) =>
          allLocationsMap.get(params.data.storageLocation) || "N/A",
      },
      {
        field: "lastSafetyInspectionDate",
        headerName: "Last Inspection",
        headerAlign: "center",
        minWidth: 150,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        field: "inspectedBy",
        headerName: "Inspected By",
        headerAlign: "center",
        minWidth: 150,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          orgUsersMap.get(params.data.inspectedBy) || "N/A",
      },
      {
        field: "action",
        headerName: "Action",
        cellStyle: { padding: "10px 15px 0 0", textAlign: "left" },
        headerStyle: { backgroundColor: "#e9f5ff" },
        minWidth: 90,
        maxWidth: 110,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 pt-2">
            <button
              onClick={() => handleEdit(params.data)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Edit Storage & Handling Record"
            >
              <img
                className="iconTd w-5 h-5"
                src={images.editIcon}
                alt="edit"
              />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete Storage & Handling Record"
            >
              <Trash2 className="text-red-500" size={23} />
            </button>
          </div>
        ),
      },
    ],
    [materialDataMap, allLocationsMap, orgUsersMap]
  );

  const defaultColDef = useMemo<any>(
    () => ({
      editable: false,
    }),
    []
  );

  const theme = useMemo<any>(() => gridTheme, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Storage & Handling Records</h2>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={() => {
              setModalOpen(true);
              setIsEditMode(false);
              setFormData(emptyForm);
            }}
          >
            <Plus size={20} /> Add Record
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={loading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={5}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">
                  No storage & handling records added yet
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        closeButton={false}
        modalClass="bg-white w-full max-w-4xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
      >
        <div className="pt-6 px-6 flex-1 overflow-y-auto">
          <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
            {isEditMode
              ? "Update Storage & Handling"
              : "Add Storage & Handling"}
          </h2>
          <form
            onSubmit={handleSubmit}
            id="storageHandlingForm"
            className="flex flex-col gap-6 pb-16"
          >
            {/* GRID FOR PAIRED INPUTS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Material Name */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="materialName"
                  className="text-sm font-medium text-gray-700"
                >
                  Material Name <span className="text-red-500">*</span>
                </label>
                <select
                  id="materialName"
                  name="materialName"
                  value={formData.materialName}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  aria-label="Material Name"
                >
                  <option value="">Select Material</option>
                  {hazardousMaterials.map((m) => (
                    <option key={m._id} value={m._id}>
                      {m.materialName}
                    </option>
                  ))}
                </select>
              </div>
              {/* Storage Location */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="storageLocation"
                  className="text-sm font-medium text-gray-700"
                >
                  Storage Location <span className="text-red-500">*</span>
                </label>
                <select
                  id="storageLocation"
                  name="storageLocation"
                  value={formData.storageLocation}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  aria-label="Storage Location"
                >
                  <option value="">Select Storage Location</option>
                  {allLocations.map((l) => (
                    <option key={l._id} value={l._id}>
                      {l.room} {l.floorNumber ? `- ${l.floorNumber}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              {/* Temperature */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="temperature"
                  className="text-sm font-medium text-gray-700"
                >
                  Temperature (°C)
                </label>
                <input
                  id="temperature"
                  name="temperature"
                  type="number"
                  min={-100}
                  max={100}
                  value={formData.temperature}
                  onChange={handleChange}
                  placeholder="Temperature in Celsius"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  aria-label="Temperature"
                />
              </div>
              {/* Humidity */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="humidity"
                  className="text-sm font-medium text-gray-700"
                >
                  Humidity (%)
                </label>
                <input
                  id="humidity"
                  name="humidity"
                  type="number"
                  min={0}
                  max={100}
                  value={formData.humidity}
                  onChange={handleChange}
                  placeholder="Humidity in percentage"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  aria-label="Humidity"
                />
              </div>
              {/* Container Type */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="containerType"
                  className="text-sm font-medium text-gray-700"
                >
                  Container Type
                </label>
                <select
                  id="containerType"
                  name="containerType"
                  value={formData.containerType}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  aria-label="Container Type"
                >
                  <option value="">Select Container Type</option>
                  {containerTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              {/* Last Safety Inspection Date */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="lastSafetyInspectionDate"
                  className="text-sm font-medium text-gray-700"
                >
                  Last Safety Inspection Date
                </label>
                <input
                  id="lastSafetyInspectionDate"
                  name="lastSafetyInspectionDate"
                  type="date"
                  value={formData.lastSafetyInspectionDate}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  aria-label="Last Safety Inspection Date"
                />
              </div>
              {/* Inspected By */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="inspectedBy"
                  className="text-sm font-medium text-gray-700"
                >
                  Inspected By
                </label>
                <select
                  id="inspectedBy"
                  name="inspectedBy"
                  value={formData.inspectedBy}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  aria-label="Inspected By"
                >
                  <option value="">Select Inspector</option>
                  {orgUsersData.map((u: OrgUserType) => (
                    <option key={u._id as string} value={u._id as string}>
                      {u.firstName} {u.lastName} - {u.email}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* Storage Conditions - full width */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="storageConditions"
                className="text-sm font-medium text-gray-700"
              >
                Storage Conditions
              </label>
              <textarea
                id="storageConditions"
                name="storageConditions"
                rows={3}
                value={formData.storageConditions}
                onChange={handleChange}
                placeholder="Describe temperature, humidity, ventilation, etc."
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                aria-label="Storage Conditions"
              />
            </div>
            {/* PPE Required - checkboxes */}
            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm font-medium text-gray-700">
                PPE Required
              </legend>
              <div className="flex flex-wrap gap-4">
                {ppeOptions.map((ppe) => (
                  <label key={ppe} className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="ppeRequired"
                      value={ppe}
                      checked={formData.ppeRequired.includes(ppe)}
                      onChange={handleChange}
                      className="form-checkbox"
                    />
                    {ppe}
                  </label>
                ))}
              </div>
            </fieldset>
            {/* Remarks - full width */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="remarks"
                className="text-sm font-medium text-gray-700"
              >
                Remarks
              </label>
              <textarea
                id="remarks"
                name="remarks"
                rows={3}
                value={formData.remarks}
                onChange={handleChange}
                placeholder="Additional notes"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                aria-label="Remarks"
              />
            </div>
          </form>
        </div>

        {/* Sticky modal footer with actions */}
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={handleModalClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="storageHandlingForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            {isEditMode ? "Update" : "Add"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default StorageHandlingRecords;
